"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";
function BlogPostsCarousel() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="mx-auto max-w-7xl px-6">
        <Carousel opts={{
        align: "start",
        loop: true
      }}>
          <CarouselContent>
            {[...Array(3)].map((_, i) => <CarouselItem key={i} data-index={i} className="md:basis-1/2 lg:basis-1/3">
                <Card className="h-full bg-white/5 border-border">
                  <div className="h-64 bg-muted/30 animate-pulse" />
                  <CardHeader>
                    <div className="h-6 bg-muted/30 animate-pulse rounded" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-4 bg-muted/30 animate-pulse rounded" />
                  </CardContent>
                </Card>
              </CarouselItem>)}
          </CarouselContent>
          <CarouselPrevious className="bg-accent hover:bg-accent-hover text-white border-0 shadow-medium -left-4 lg:-left-12" />
          <CarouselNext className="bg-accent hover:bg-accent-hover text-white border-0 shadow-medium -right-4 lg:-right-12" />
        </Carousel>
      </div>;
  }
  if (error) {
    return <div className="mx-auto max-w-7xl px-6 text-center">
        <p className="text-muted-foreground">Unable to load blog posts at this time.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="mx-auto max-w-7xl px-6 text-center">
        <p className="text-muted-foreground">No blog posts available yet.</p>
      </div>;
  }
  return <div className="mx-auto max-w-7xl px-6">
      <Carousel opts={{
      align: "start",
      loop: true
    }}>
        <CarouselContent>
          {posts.map((post, i) => <CarouselItem key={post.id} data-index={i} className="md:basis-1/2 lg:basis-1/3">
              <Card className="h-full bg-white/5 border-border hover:shadow-strong transition-all duration-[150ms] ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden group">
                {post.featuredImage && <div className="relative h-64 overflow-hidden">
                    <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-[150ms] ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-105" />
                  </div>}
                <CardHeader>
                  <CardTitle className="text-white font-display text-lg line-clamp-2">
                    {post.title}
                  </CardTitle>
                  {post.date && <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                      <SafeHydrate><Calendar className="w-4 h-4" /></SafeHydrate>
                      <time>{new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}</time>
                    </div>}
                </CardHeader>
                <CardContent>
                  {post.excerpt && <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                      {post.excerpt.replace(/<[^>]*>/g, "")}
                    </p>}
                  {post.link && <Link to={post.link} className="inline-flex items-center text-accent hover:text-accent-hover font-semibold text-sm transition-colors duration-[120ms]">
                      Read Article →
                    </Link>}
                </CardContent>
              </Card>
            </CarouselItem>)}
        </CarouselContent>
        <CarouselPrevious className="bg-accent hover:bg-accent-hover text-white border-0 shadow-medium -left-4 lg:-left-12 transition-all duration-[120ms]" />
        <CarouselNext className="bg-accent hover:bg-accent-hover text-white border-0 shadow-medium -right-4 lg:-right-12 transition-all duration-[120ms]" />
      </Carousel>
    </div>;
}
export default function BlogPreview() {
  return <section id="blog-preview" className="dark bg-background text-foreground py-20 px-4">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="font-display text-4xl font-bold text-white mb-4">
            Market Insights & Updates
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Stay informed with the latest commodity trends, market analysis, and industry news from our experts.
          </p>
        </div>

        <PostsProvider className="blog-posts-carousel-wrapper" wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia"]}>
          <BlogPostsCarousel />
        </PostsProvider>
      </div>
    </section>;
}